import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Button, Space, Typography, Tag, Spin, Alert } from 'antd';
import { CheckCircleOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import httpService from 'services/http.service';
import { API_URLS } from 'config/constants';

const { Title, Text } = Typography;

interface TikTokStatus {
  connected: boolean;
  openId?: string;
}

const SocialSettings = () => {
  const location = useLocation();
  const [status, setStatus] = useState<TikTokStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [flash, setFlash] = useState<'connected' | 'error' | null>(null);

  const fetchStatus = async () => {
    try {
      const res = await httpService.get<TikTokStatus>(API_URLS.SOCIAL.TIKTOK_STATUS);
      setStatus(res.data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tiktokParam = params.get('tiktok');
    if (tiktokParam === 'connected') setFlash('connected');
    if (tiktokParam === 'error') setFlash('error');
    fetchStatus();
  }, [location.search]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await httpService.get<{ url: string }>(API_URLS.SOCIAL.TIKTOK_AUTH_URL);
      window.location.href = res.data.url;
    } catch {
      setConnecting(false);
    }
  };

  const renderButton = useMemo(() => {
    if (loading) return <Spin size='small' />;
    if (status?.connected) return <Tag color='success' icon={<CheckCircleOutlined />}>Connected</Tag>;
    return <Tag color='default' icon={<LinkOutlined />}>Connect TikTok</Tag>;
  }, []);

  return (
    <div style={{ maxWidth: 600, margin: '24px auto' }}>
      <Title level={4}>Social Media Settings</Title>

      {flash === 'connected' && (
        <Alert
          message='TikTok connected successfully!'
          type='success'
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setFlash(null)}
        />
      )}
      {flash === 'error' && (
        <Alert
          message='Failed to connect TikTok. Please try again.'
          type='error'
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setFlash(null)}
        />
      )}

      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <svg viewBox='0 0 24 24' width={28} height={28} fill='currentColor'>
              <path d='M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.79a4.85 4.85 0 01-1.01-.1z' />
            </svg>
            <div>
              <Text strong>TikTok</Text>
              <br />
              { renderButton }
            </div>
          </Space>

          <Button
            type={status?.connected ? 'default' : 'primary'}
            icon={<LinkOutlined />}
            loading={connecting}
            disabled={loading}
            onClick={handleConnect}
          >
            {status?.connected ? 'Reconnect' : 'Connect TikTok'}
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default SocialSettings;
